import { useNavigate, useLocation } from "react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FooterLinkGroup from "./components/FooterLinkGroup";
import namedLogo from "../../assets/images/logo-header@2x.png";
import styles from "./Footer.module.scss";
import { useLanguage } from "context";

const Footer = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("global");
  const [email, setEmail] = useState("");
  const [newsletterFormUrl, setNewsletterFormUrl] = useState("");
  const [blogUrl, setBlogUrl] = useState("");

  const isHomepage = location.pathname === "/";

  useEffect(() => {
    // Newsletter form URLs
    const formUrls = {
      com: "//chooseandwork.us14.list-manage.com/subscribe?u=fd07364dfadb6f746a4432f54&id=6bc77d787b",
      be: "//chooseandwork.us14.list-manage.com/subscribe/post?u=fd07364dfadb6f746a4432f54&id=c3e44a6298",
      fr: "//chooseandwork.us14.list-manage.com/subscribe/post?u=fd07364dfadb6f746a4432f54&id=c3e44a6298",
      uk: "//chooseandwork.us14.list-manage.com/subscribe?u=fd07364dfadb6f746a4432f54&id=6bc77d787b",
      es: "//chooseandwork.us14.list-manage.com/subscribe?u=fd07364dfadb6f746a4432f54&id=6bc77d787b",
    };

    // Or if you have specific URLs for each language:
    const specificBlogUrls = {
      en: "https://blog.chooseandwork.com", // English
      be: "https://blog.chooseandwork.fr", // French
      fr: "https://blog.chooseandwork.fr", // French
      uk: "https://blog.chooseandwork.com", // English
      es: "https://blog.chooseandwork.es", // Spanish
    };

    setNewsletterFormUrl(formUrls[language] || formUrls.fr);
    setBlogUrl(specificBlogUrls[language] || specificBlogUrls.fr); // Use specificBlogUrls if you have language-specific blogs
  }, [language]);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email && newsletterFormUrl) {
      const fullUrl = `${newsletterFormUrl}&EMAIL=${encodeURIComponent(email)}`;
      window.open(fullUrl, "_blank");
      setEmail("");
    }
  };

  return (
    <footer className={styles.footerContainer}>
      <div className={`${styles.footer} pageContainer`}>
        <section className={styles.footerGrid1}>
          <FooterLinkGroup
            title={t("footer.our-places")}
            links={[
              { text: t("footer.favorites"), url: "/search?type=favorite" },
              { text: t("footer.lastMinute"), url: "/search?type=last_minute" },
              { text: t("footer.new"), url: "/search?type=new" },
              { text: t("footer.bestnote"), url: "/search?type=best_note" },
            ]}
          />

          <FooterLinkGroup
            title={t("footer.about")}
            links={[
              { text: t("navbar.who_we_are"), url: "/static/qui-sommes-nous" },
              { text: t("footer.contact"), url: "/static/contact" },
              { text: t("footer.terms"), url: "/static/cgu" },
              { text: t("footer.faq"), url: "/faq" },
            ]}
          />

          <FooterLinkGroup
            title={t("footer.useful-information")}
            links={[
              { text: t("footer.blog"), url: blogUrl }, // Use dynamic blog URL
              { text: t("footer.press"), url: "/static/press" },
              { text: t("footer.sitemap"), url: "/static/plan" },
              { text: t("footer.searchesList"), url: "/searches" },
            ]}
          />

          <FooterLinkGroup
            title={t("footer.affiliate-program")}
            links={[
              {
                text: t("footer.memberPlus"),
                url: "/landing/MEMBERPLUS?_ga=2.208507216.353212696.1728207871-1412234691.1709649265",
              },
              {
                text: t("footer.memberPlusCities"),
                url: "/landing/membres-privileges",
              },
            ]}
          />

          <div className={styles.socialContainer}>
            <div className={styles.socialsText}>{t("footer.follow-us")}</div>
            <nav className={styles.socialMediaLinks} aria-label="social-media">
              <a
                href="https://www.facebook.com/Choose-and-Work-1852943138268164/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.fb}
                aria-label="Facebook"
              />
              <a
                href="https://www.instagram.com/chooseandwork"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.ig}
                aria-label="Instagram"
              />
              <a
                href="https://twitter.com/Chooseandwork"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.twitter}
                aria-label="Twitter"
              />
              <a
                href="https://www.linkedin.com/company/choose-and-work"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.linkedin}
                aria-label="LinkedIn"
              />
            </nav>
          </div>
        </section>

        <div className={styles.footerGrid2}>
          {/* Empty section for grid alignment */}
          <section aria-hidden="true"></section>

          <section className={styles.logoSection}>
            <span className={styles.copyrightText}>
              {" "}
              {t("footer.copyrightText", { year: new Date().getFullYear() })}
            </span>
            <img
              src={namedLogo}
              className={styles.footerLogo}
              alt="Choose and Work logo"
              loading="lazy"
            />
            <div className={styles.textBox}>
              <a className={styles.textBoxTel} href="tel:01 80 522 522">
                Tél: +33 (0) 1 80 522 522
              </a>
              <span>Belgique : 156, avenue de Floréal – 1180 BRUXELLES</span>
              <span>France : 3, rue du Colonel Moll – 75017 PARIS</span>
            </div>
            <div className={styles.confidentialityLinks}>
              <a href="/static/cgu" className={styles.confidentialityLink}>
                {t("footer.terms")}
              </a>
              <a href="/static/politique-confidentialite" className={styles.confidentialityLink}>
                {t("footer.privacyPolicy")}
              </a>
              <a href="/static/mentions-legales" className={styles.confidentialityLink}>
                {t("footer.legalMentions")}
              </a>
            </div>
          </section>

          {/* Conditionally render newsletter section */}
          {!isHomepage && (
            <section className={styles.newsletterSection}>
              <span
                className={styles.heading}
                dangerouslySetInnerHTML={{ __html: t("footer.newsletter") }}
              />
              <form
                className={styles.newsletterForm}
                onSubmit={handleNewsletterSubmit}
                id="mc-embedded-subscribe-form"
              >
                <input
                  type="email"
                  name="EMAIL"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("footer.email")}
                  className={styles.newsletterInput}
                  aria-label="Email"
                  required
                />
                <button
                  className={styles.newsletterButton}
                  type="submit"
                  disabled={!email}
                >
                  {t("buttons.register")}
                </button>
              </form>
            </section>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
